// StockMaster.jsx
// Stock master management — add, view, deactivate stocks
// stock_id shown in table (auto-generated, not editable)

import React, { useState, useEffect } from 'react';
import axios from 'axios';

import API_BASE from '../config';
import { api } from '../api';

function StockMaster() {
  const [activeTab, setActiveTab]   = useState('add');  // 'add' | 'view'

  // form tab state
  const [formError, setFormError]   = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [editMode, setEditMode]     = useState(false);

  // view tab state
  const [stocks, setStocks]         = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError]   = useState(null);
  const [listSuccess, setListSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [form, setForm] = useState({
    symbol:               '',
    name:                 '',
    sector:               '',
    isin:                 '',
    z_symbol:             '',
    business_description: '',
    start_date_1d:        '',
    start_date_5min:      '',
    u_symbol:             '',
    instrument_type:      'stock',
    default_download:     'breeze',
    icici_symbol:         '',
    expiry_date:          '',
  });

  useEffect(() => {
    if (activeTab === 'view') loadStocks();
  }, [activeTab]);

  const loadStocks = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await api.get(`/stocks`);
      setStocks(res.data);
    } catch (err) {
      setListError('Failed to load stocks');
    }
    setListLoading(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.symbol.trim() || !form.name.trim()) {
      setFormError('Symbol and Name are required');
      return;
    }
    setFormLoading(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      const res = await api.post(`/stocks`, null, {
        params: {
          symbol:               form.symbol.toUpperCase().trim(),
          name:                 form.name.trim(),
          sector:               form.sector.trim() || null,
          isin:                 form.isin.trim() || null,
          z_symbol:             form.z_symbol.trim() || null,
          business_description: form.business_description.trim() || null,
          start_date_1d:        form.start_date_1d || null,
          start_date_5min:      form.start_date_5min || null,
          u_symbol:             form.u_symbol.trim() || null,
          instrument_type:      form.instrument_type  || 'stock',
          default_download:     form.default_download || 'breeze',
          icici_symbol:         form.icici_symbol.trim() || null,
          expiry_date:          form.expiry_date || null,
        }
      });
      const stockId = res.data.stock_id;
      setFormSuccess(`${form.symbol.toUpperCase()} saved — Stock ID: ${stockId}`);
      setForm({
        symbol: '', name: '', sector: '',
        isin: '', z_symbol: '', business_description: '',
        start_date_1d: '', start_date_5min: '',
        u_symbol: '', instrument_type: 'stock', default_download: 'breeze',
        icici_symbol: '', expiry_date: '',
      });
      setEditMode(false);
      
    } catch (err) {
      setFormError('Failed to save stock');
    }
    setFormLoading(false);
  };

  const handleDeactivate = async (symbol) => {
    if (!window.confirm(`Deactivate ${symbol}?`)) return;
    try {
      await api.delete(`/stocks/${symbol}`);
      setListSuccess(`${symbol} deactivated`);
      await loadStocks();
    } catch (err) {
      setListError('Failed to deactivate stock');
    }
  };

  const handleEdit = (stock) => {
    setEditMode(true);
    setForm({
      symbol:               stock.symbol,
      name:                 stock.name,
      sector:               stock.sector || '',
      isin:                 stock.isin || '',
      z_symbol:             stock.z_symbol || '',
      business_description: stock.business_description || '',
      start_date_1d:        stock.start_date_1d || '',
      start_date_5min:      stock.start_date_5min || '',
      u_symbol:             stock.u_symbol || '',
      instrument_type:      stock.instrument_type || 'stock',
      default_download:     stock.default_download || 'breeze',
      icici_symbol:         stock.icici_symbol || '',
      expiry_date:          stock.expiry_date || '',
    });
    setFormError(null);
    setFormSuccess(null);
    setActiveTab('add');   // ← switch to add tab with form pre-filled
  };

  const filteredStocks = stocks.filter((s) =>
    s.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.sector || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Stock Master</h3>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'add' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('add')}
        >
          {editMode ? 'Edit Stock' : 'Add Stock'}
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'view' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('view')}
        >
          View Stocks
        </button>
      </div>

      {/* Add / Edit Tab */}
      {activeTab === 'add' && (
        <div style={styles.card}>

          {formError && (
            <div style={styles.error}>
              {formError}
              <button onClick={() => setFormError(null)} style={styles.closeBtn}>×</button>
            </div>
          )}
          {formSuccess && (
            <div style={styles.success}>
              {formSuccess}
              <button onClick={() => setFormSuccess(null)} style={styles.closeBtn}>×</button>
            </div>
          )}

          <div style={styles.formGrid}>

            <div style={styles.formField}>
              <label style={styles.label}>Symbol * (internal key)</label>
              <input
                style={{
                  ...styles.input,
                  backgroundColor: editMode ? '#f5f5f5' : '#fff',
                }}
                name="symbol"
                placeholder="e.g. RELIND"
                value={form.symbol}
                onChange={handleChange}
                readOnly={editMode}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>ICICI Symbol (Breeze stock_code)</label>
              <input
                style={styles.input}
                name="icici_symbol"
                placeholder="e.g. RELIND — required for Breeze download"
                value={form.icici_symbol}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Name *</label>
              <input
                style={styles.input}
                name="name"
                placeholder="e.g. Reliance Industries Ltd"
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Zerodha Symbol</label>
              <input
                style={styles.input}
                name="z_symbol"
                placeholder="e.g. RELIANCE"
                value={form.z_symbol}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Sector</label>
              <input
                style={styles.input}
                name="sector"
                placeholder="e.g. Energy"
                value={form.sector}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>ISIN</label>
              <input
                style={styles.input}
                name="isin"
                placeholder="e.g. INE002A01018"
                value={form.isin}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Upstox Symbol (instrument_key)</label>
              <input
                style={styles.input}
                name="u_symbol"
                placeholder="e.g. NSE_EQ|INE002A01018 or MCX_FO|488788"
                value={form.u_symbol}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Instrument Type</label>
              <select
                style={styles.input}
                name="instrument_type"
                value={form.instrument_type}
                onChange={handleChange}
              >
                <option value="stock">Stock</option>
                <option value="commodity">Commodity</option>
              </select>
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Default Download</label>
              <select
                style={styles.input}
                name="default_download"
                value={form.default_download}
                onChange={handleChange}
              >
                <option value="breeze">Breeze</option>
                <option value="upstox">Upstox</option>
              </select>
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>
                Expiry Date
                <span style={styles.optionalBadge}>Futures only</span>
              </label>
              <input
                style={styles.input}
                name="expiry_date"
                placeholder="YYYY-MM-DD — leave empty for equities"
                value={form.expiry_date}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Start Date 1D</label>
              <input
                style={styles.input}
                name="start_date_1d"
                placeholder="YYYY-MM-DD"
                value={form.start_date_1d}
                onChange={handleChange}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.label}>Start Date 5min</label>
              <input
                style={styles.input}
                name="start_date_5min"
                placeholder="YYYY-MM-DD"
                value={form.start_date_5min}
                onChange={handleChange}
              />
            </div>

            <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
              <label style={styles.label}>Business Description</label>
              <textarea
                style={{ ...styles.input, height: '60px', resize: 'vertical' }}
                name="business_description"
                placeholder="Brief business description..."
                value={form.business_description}
                onChange={handleChange}
              />
            </div>

          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              style={styles.btnPrimary}
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading ? 'Saving...' : editMode ? 'Update Stock' : 'Add Stock'}
            </button>
            {editMode && (
              <button
                style={styles.btnSecondary}
                onClick={() => {
                  setEditMode(false);
                  setFormError(null);
                  setFormSuccess(null);
                  setForm({
                    symbol: '', name: '', sector: '',
                    isin: '', z_symbol: '', business_description: '',
                    start_date_1d: '', start_date_5min: '',
                    u_symbol: '', instrument_type: 'stock', default_download: 'breeze',
                  });
                }}
              >
                Cancel
              </button>
            )}
          </div>

        </div>
      )}

      {/* View Stocks Tab */}
      {activeTab === 'view' && (
        <div style={styles.card}>

          {listError && (
            <div style={styles.error}>
              {listError}
              <button onClick={() => setListError(null)} style={styles.closeBtn}>×</button>
            </div>
          )}
          {listSuccess && (
            <div style={styles.success}>
              {listSuccess}
              <button onClick={() => setListSuccess(null)} style={styles.closeBtn}>×</button>
            </div>
          )}

          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>
              All Stocks ({filteredStocks.length})
            </div>
            <input
              style={styles.searchInput}
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {listLoading && <div style={styles.empty}>Loading...</div>}

          {!listLoading && filteredStocks.length === 0 && (
            <div style={styles.empty}>No stocks found</div>
          )}

          {filteredStocks.length > 0 && (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Symbol</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>ICICI</th>
                    <th style={styles.th}>Upstox</th>
                    <th style={styles.th}>Z Symbol</th>
                    <th style={styles.th}>Sector</th>
                    <th style={styles.th}>ISIN</th>
                    <th style={styles.th}>Start 1D</th>
                    <th style={styles.th}>Start 5min</th>
                    <th style={styles.th}>Expiry</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStocks.map((stock) => (
                    <tr key={stock.symbol} style={styles.tr}>
                      <td style={styles.tdId}>{stock.stock_id}</td>
                      <td style={styles.tdBold}>{stock.symbol}</td>
                      <td style={styles.td}>{stock.name}</td>
                      <td style={styles.td}>{stock.icici_symbol || '—'}</td>
                      <td style={styles.td}>{stock.u_symbol || '—'}</td>
                      <td style={styles.td}>{stock.z_symbol || '—'}</td>
                      <td style={styles.td}>{stock.sector || '—'}</td>
                      <td style={styles.td}>{stock.isin || '—'}</td>
                      <td style={styles.td}>{stock.start_date_1d || '—'}</td>
                      <td style={styles.td}>{stock.start_date_5min || '—'}</td>
                      <td style={styles.td}>{stock.expiry_date || '—'}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            style={styles.btnEdit}
                            onClick={() => handleEdit(stock)}
                          >Edit</button>
                          <button
                            style={styles.btnDelete}
                            onClick={() => handleDeactivate(stock.symbol)}
                          >Deactivate</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}

    </div>
  );
}

const styles = {
  container: {
    padding:   '16px',
    overflowY: 'auto',
    height:    '100%',
    background:'#f5f5f5',
    color:     '#131722',
  },
  title: {
    fontSize:     '15px',
    fontWeight:   'bold',
    marginBottom: '16px',
  },
  card: {
    backgroundColor: '#ffffff',
    border:          '1px solid #e0e0e0',
    borderRadius:    '8px',
    padding:         '16px',
    marginBottom:    '16px',
    boxShadow:       '0 1px 3px rgba(0,0,0,0.08)',
  },
  cardTitle: {
    color:         '#666',
    fontSize:      '11px',
    textTransform: 'uppercase',
    marginBottom:  '12px',
    letterSpacing: '0.5px',
    fontWeight:    'bold',
  },
  cardTitleRow: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    marginBottom:   '12px',
  },
  formGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 '10px',
    marginBottom:        '12px',
  },
  formField: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
  label: {
    fontSize: '12px',
    color:    '#666',
  },
  input: {
    padding:      '7px 10px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    fontSize:     '13px',
    color:        '#131722',
    background:   '#fff',
    width:        '100%',
    boxSizing:    'border-box',
  },
  searchInput: {
    padding:      '5px 10px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    fontSize:     '13px',
    width:        '200px',
  },
  btnPrimary: {
    padding:         '8px 20px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '13px',
    fontWeight:      'bold',
  },
  btnSecondary: {
    padding:         '8px 16px',
    backgroundColor: '#f0f0f0',
    color:           '#333',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '13px',
  },
  btnEdit: {
    padding:         '3px 8px',
    backgroundColor: '#e8f0fe',
    color:           '#2962ff',
    border:          '1px solid #c5d8fd',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontSize:        '11px',
  },
  btnDelete: {
    padding:         '3px 8px',
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    border:          '1px solid #ffcdd2',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontSize:        '11px',
  },
  tableWrapper: { overflowX: 'auto' },
  table: {
    width:          '100%',
    borderCollapse: 'collapse',
    fontSize:       '13px',
  },
  th: {
    textAlign:     'left',
    padding:       '8px 12px',
    borderBottom:  '2px solid #e0e0e0',
    color:         '#666',
    fontSize:      '11px',
    fontWeight:    'bold',
    textTransform: 'uppercase',
  },
  tr:     { borderBottom: '1px solid #f0f0f0' },
  td:     { padding: '8px 12px', color: '#333' },
  tdBold: { padding: '8px 12px', color: '#131722', fontWeight: 'bold' },
  tdId:   { padding: '8px 12px', color: '#999', fontSize: '11px' },
  empty:  { color: '#999', fontSize: '13px', padding: '12px 0' },
  error: {
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    padding:         '10px 14px',
    borderRadius:    '4px',
    marginBottom:    '12px',
    fontSize:        '13px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #ffcdd2',
  },
  success: {
    backgroundColor: '#f0fff4',
    color:           '#2e7d32',
    padding:         '10px 14px',
    borderRadius:    '4px',
    marginBottom:    '12px',
    fontSize:        '13px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #c8e6c9',
  },
  closeBtn: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '16px',
    color:      'inherit',
  },

  tabs: {
    display:      'flex',
    gap:          '2px',
    marginBottom: '16px',
    borderBottom: '2px solid #e0e0e0',
  },
  tab: {
    padding:         '8px 20px',
    backgroundColor: 'transparent',
    color:           '#666',
    border:          'none',
    borderBottom:    '2px solid transparent',
    marginBottom:    '-2px',
    cursor:          'pointer',
    fontSize:        '13px',
    fontWeight:      'bold',
  },
  tabActive: {
    color:       '#2962ff',
    borderBottom:'2px solid #2962ff',
  },
};

export default StockMaster;